import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useTranslation, LANGUAGES } from "@/i18n";

const go = (link) => {
  if (link.route) {
    window.location.href = link.href;
    return;
  }
  if (window.location.pathname !== "/") {
    window.location.href = `/${link.href}`;
    return;
  }
  const target = document.querySelector(link.href);
  if (!target) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -72, duration: 1.4 });
  } else {
    target.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, setLang, t } = useTranslation();

  const links = [
    { label: t("nav.home"), href: "#accueil" },
    { label: t("nav.mission"), href: "#manifeste" },
    { label: t("nav.expertises"), href: "#expertises" },
    { label: t("nav.offres"), href: "#offres" },
    { label: t("nav.team"), href: "#equipe" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAndClose = (link) => {
    setMenuOpen(false);
    go(link);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || menuOpen ? "glass border-b border-gold/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20 h-[72px] flex items-center justify-between">
        <button
          onClick={() => goAndClose({ href: "#accueil" })}
          className="flex items-center gap-3 group"
          data-testid="header-logo"
        >
          <img
            src="/logo-mark.png"
            alt="e².law"
            className="w-8 h-8 group-hover:scale-110 transition-transform duration-300"
          />
          <span className="font-serif text-xl tracking-wide text-[#FAF8F5]">
            e²<span className="text-gold">.law</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="header-nav">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors duration-300 font-sans"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-2" data-testid="lang-switcher">
            {LANGUAGES.map((l, i) => (
              <span key={l.code} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#9BA1B0] text-[10px]">·</span>}
                <button
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-switch-${l.code}`}
                  className={`text-[11px] uppercase tracking-[0.15em] font-mono transition-colors duration-300 ${
                    lang === l.code ? "text-gold" : "text-[#9BA1B0] hover:text-[#FAF8F5]"
                  }`}
                >
                  {l.label}
                </button>
              </span>
            ))}
          </div>
          <span className="hidden xl:flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#9BA1B0] font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            {t("nav.location")}
          </span>
          <button
            onClick={() => go({ href: "#contact" })}
            data-testid="header-cta-rdv"
            className="hidden sm:inline-flex items-center border border-gold/50 text-gold text-xs uppercase tracking-[0.2em] px-5 py-2.5 hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            {t("nav.cta")}
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="menu-toggle-button"
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOpen}
            className="lg:hidden text-[#FAF8F5] hover:text-gold transition-colors duration-300 p-1"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden glass border-b border-gold/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 sm:px-12 py-6 flex flex-col">
              {links.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.05 + i * 0.05 }}
                  onClick={() => goAndClose(l)}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  className="text-left font-serif text-2xl py-4 text-[#FAF8F5] hover:text-gold border-b border-white/5 last:border-0 transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.05 + links.length * 0.05 }}
                onClick={() => goAndClose({ href: "#contact" })}
                data-testid="mobile-cta-rdv"
                className="mt-5 mb-2 self-start bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-7 py-3.5 hover:bg-gold-light transition-colors duration-300"
              >
                {t("nav.cta")}
              </motion.button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
